import styled, { css, keyframes } from 'styled-components';

/* ---------- tokens locales ---------- */

const accent = 'var(--btn-primary)';
const accentSoft = 'rgba(0, 180, 216, 0.14)';
const borderSoft = 'rgba(255, 255, 255, 0.08)';
const borderStrong = 'rgba(255, 255, 255, 0.14)';
const panelBg = 'rgba(36, 52, 77, 0.72)';
const panelBgSolid = '#24344D';
const mono = "'JetBrains Mono', 'Fira Code', 'SFMono-Regular', 'Consolas', monospace";

/* utilidad: esquinas técnicas */
const techCorners = css`
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    opacity: 0.9;
  }
  &::before {
    top: -1px;
    left: -1px;
    border-top-width: 1px;
    border-left-width: 1px;
  }
  &::after {
    bottom: -1px;
    right: -1px;
    border-bottom-width: 1px;
    border-right-width: 1px;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(6, 214, 160, 0.6); }
  50% { opacity: 0.55; box-shadow: 0 0 0 6px rgba(6, 214, 160, 0); }
`;

/* ---------- contenedores principales ---------- */

export const ProfileContainer = styled.div`
  padding: 0.5rem 0 2rem;
  position: relative;

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
    background-size: 32px 32px;
    mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.6), transparent 75%);
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const ProfileHeader = styled.div`
  position: relative;
  margin-bottom: 1.75rem;
  padding: 1.25rem 1.5rem;
  background:
    linear-gradient(135deg, rgba(0, 180, 216, 0.06), transparent 55%),
    ${panelBg};
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  backdrop-filter: blur(6px);
  ${techCorners}

  @media (max-width: 768px) {
    padding: 1rem 1.1rem;
    margin-bottom: 1.25rem;
  }
  @media (max-width: 480px) {
    padding: 0.85rem 0.9rem;
    border-radius: 8px;
  }
`;

export const ProfileTitle = styled.h1`
  font-size: 0.78rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin: 0 0 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;

  &::before {
    content: '';
    display: inline-block;
    width: 22px;
    height: 1px;
    background: ${accent};
  }

  &::after {
    content: 'PROFILE://FCH-0001';
    font-family: ${mono};
    font-size: 0.68rem;
    letter-spacing: 0.1em;
    color: ${accent};
    padding: 2px 8px;
    border: 1px solid ${borderStrong};
    border-radius: 4px;
    margin-left: auto;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    letter-spacing: 0.18em;
    font-size: 0.7rem;
    &::after { display: none; }
  }
`;

export const ProfileSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1rem;
  margin: 0.25rem 0 1rem;
`;

export const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 1.25rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(6, 1fr);
    gap: 1rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const GridItem = styled.div`
  grid-column: span ${({ span }) => span || 12};
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;

  /* En el grid de 6 cols, cada span se reduce a la mitad para mantener
     2 tarjetas por fila (en lugar de 1 con espacio en blanco). */
  @media (max-width: 1200px) {
    grid-column: span ${({ span }) => Math.max(1, Math.round((span || 12) / 2))};
    gap: 1rem;
  }

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    width: 100%;
  }
`;

export const ProfileSection = styled.div`
  margin-bottom: 1.75rem;
`;

/* ---------- header del perfil (imagen + bio + meta) ---------- */

export const ProfileInfo = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr auto;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 120px 1fr;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
`;

export const ProfileImageContainer = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid ${borderStrong};
  background: #0b1320;
  flex-shrink: 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    border: 1px solid ${accent};
    z-index: 2;
  }
  &::before {
    top: 6px;
    left: 6px;
    border-right: none;
    border-bottom: none;
  }
  &::after {
    bottom: 6px;
    right: 6px;
    border-left: none;
    border-top: none;
  }

  @media (max-width: 900px) {
    width: 120px;
    height: 120px;
  }
  @media (max-width: 480px) {
    width: 96px;
    height: 96px;
  }
`;

export const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.92) contrast(1.02);
`;

export const ProfileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  min-width: 0;
`;

export const ProfileName = styled.h2`
  font-size: 1.8rem;
  margin: 0;
  color: white;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;

  &::after {
    content: 'ACTIVE';
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.18em;
    color: var(--alert-success);
    padding: 3px 7px;
    border: 1px solid rgba(6, 214, 160, 0.4);
    border-radius: 3px;
    background: rgba(6, 214, 160, 0.08);
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  @media (max-width: 480px) {
    font-size: 1.25rem;
    gap: 0.4rem;
  }
`;

export const ProfileBio = styled.p`
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.55;
  font-size: 0.95rem;
  max-width: 70ch;
`;

export const ProfileMetaList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.25rem 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.5rem 1.25rem;
`;

export const ProfileMetaItem = styled.li`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  font-size: 0.88rem;
  padding: 0.2rem 0;
  border-left: 1px solid ${borderSoft};
  padding-left: 0.75rem;
  min-width: 0;
  word-break: break-word;

  @media (max-width: 560px) {
    /* en modo centrado, los iconos siguen del lado izquierdo pero sin
       border-left para evitar la línea suelta */
    border-left: none;
    padding-left: 0;
    justify-content: center;
    text-align: left;
  }
`;

export const MetaIcon = styled.div`
  margin-right: 0.6rem;
  color: ${accent};
  font-size: 1rem;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* ---------- tarjetas genéricas ---------- */

export const ProfileCard = styled.div`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.015), transparent 40%),
    ${panelBgSolid};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 1.25rem 1.25rem 1.1rem;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.24);
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${borderStrong};
  }

  ${props => props.wide && 'grid-column: 1 / -1;'}

  ${techCorners}

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

export const CardTitle = styled.h3`
  position: relative;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin: 0 0 1rem;
  padding: 0 0 0.6rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  border-bottom: 1px solid ${borderSoft};

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${accent};
    box-shadow: 0 0 0 3px ${accentSoft};
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    font-size: 0.72rem;
    letter-spacing: 0.16em;
  }
`;

export const CardContent = styled.div`
  padding: 0.25rem 0 0;
`;

/* ---------- listas clave/valor ---------- */

export const KeyValueList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
`;

export const KeyValueItem = styled.div`
  padding: 0.85rem 1rem;
  background: rgba(11, 19, 32, 0.55);
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 4px;
`;

export const KeyName = styled.div`
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin-bottom: 0.35rem;
  font-family: ${mono};
`;

export const ValueText = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: white;
`;

export const SectionTitle = styled.h3`
  margin-bottom: 1.25rem;
  color: white;
  font-size: 0.85rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  padding: 0 0 0.4rem;
  border-bottom: 1px solid ${borderStrong};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '▸';
    color: ${accent};
  }
`;

/* ---------- tags / cloud ---------- */

export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #cfe9f4;
  background: rgba(0, 180, 216, 0.08);
  border: 1px solid rgba(0, 180, 216, 0.25);
  padding: 0.3rem 0.7rem;
  border-radius: 3px;
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  font-family: ${mono};
  transition: background 0.18s ease, border-color 0.18s ease;

  &::before {
    content: '#';
    color: ${accent};
    opacity: 0.8;
  }

  &:hover {
    background: rgba(0, 180, 216, 0.14);
    border-color: rgba(0, 180, 216, 0.4);
  }

  @media (max-width: 480px) {
    font-size: 0.72rem;
    padding: 0.25rem 0.55rem;
  }
`;

export const TagCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

/* ---------- gauges de ideología ---------- */

export const IdeologyChart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const IdeologyAxis = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 110px;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 520px) {
    /* dos rows: fila superior con labels izq/der, fila inferior con la barra.
       row-gap 1.5rem deja espacio para el AxisValue flotante (-18px). */
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem 0.75rem;
  }
`;

export const IdeologyLabel = styled.span`
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  font-family: ${mono};

  &:first-child {
    text-align: left;
  }
  &:last-child {
    text-align: right;
  }

  @media (max-width: 520px) {
    font-size: 0.66rem;
    letter-spacing: 0.12em;
  }
`;

export const IdeologyBar = styled.div`
  position: relative;
  height: 22px;
  background:
    linear-gradient(
      90deg,
      transparent 0 calc(25% - 1px),
      ${borderSoft} calc(25% - 1px) 25%,
      transparent 25% calc(50% - 1px),
      ${borderStrong} calc(50% - 1px) 50%,
      transparent 50% calc(75% - 1px),
      ${borderSoft} calc(75% - 1px) 75%,
      transparent 75%
    ),
    linear-gradient(90deg, rgba(0, 180, 216, 0.08), rgba(6, 214, 160, 0.08));
  border: 1px solid ${borderSoft};
  border-radius: 3px;

  @media (max-width: 520px) {
    /* en layout 2-col las labels van arriba (col 1 y 2) y la barra abajo
       ocupando todo el ancho */
    order: 3;
    grid-column: 1 / -1;
  }
`;

export const IdeologyMarker = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 22px;
  background: ${accent};
  box-shadow: 0 0 12px rgba(0, 180, 216, 0.6);
  border-radius: 2px;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: -6px;
    width: 1px;
    height: 34px;
    background: ${accent};
    opacity: 0.55;
    transform: translateX(-50%);
  }
`;

/* ---------- timeline ---------- */

export const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  position: relative;
  padding-left: 0.25rem;
`;

export const TimelineItem = styled.div`
  position: relative;
  padding-left: 1.5rem;
  border-left: 1px solid ${borderStrong};
  padding-bottom: 0.25rem;

  &::before {
    content: '';
    position: absolute;
    left: -5px;
    top: 4px;
    width: 9px;
    height: 9px;
    background: ${accent};
    border-radius: 50%;
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.15);
  }
`;

export const TimelineDate = styled.div`
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${accent};
  margin-bottom: 0.2rem;
  font-family: ${mono};
`;

export const TimelineTitle = styled.h4`
  font-size: 0.95rem;
  margin: 0 0 0.2rem;
  color: white;
  font-weight: 600;
`;

export const TimelineDescription = styled.p`
  font-size: 0.85rem;
  color: var(--text-disabled);
  margin: 0;
  line-height: 1.5;
`;

/* ---------- propuestas / listas con icono ---------- */

export const ProposalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  counter-reset: proposal;
`;

export const ProposalItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.7rem 0.8rem;
  background: rgba(11, 19, 32, 0.45);
  border: 1px solid ${borderSoft};
  border-radius: 4px;
  transition: border-color 0.18s ease, transform 0.18s ease;
  counter-increment: proposal;
  min-width: 0;

  &::before {
    content: counter(proposal, decimal-leading-zero);
    font-family: ${mono};
    font-size: 0.72rem;
    color: ${accent};
    letter-spacing: 0.1em;
    flex-shrink: 0;
    min-width: 24px;
  }

  &:hover {
    border-color: rgba(0, 180, 216, 0.3);
    transform: translateX(2px);
  }

  @media (max-width: 480px) {
    gap: 0.55rem;
    padding: 0.6rem 0.65rem;
    &::before { min-width: 20px; font-size: 0.68rem; }
  }
`;

export const ProposalIcon = styled.div`
  color: ${accent};
  font-size: 0.95rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
`;

export const ProposalText = styled.p`
  flex: 1;
  margin: 0;
  line-height: 1.45;
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

/* contenedor padre para lista numerada */
export const NumberedList = styled.div`
  counter-reset: proposal;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

/* ---------- redes sociales ---------- */

export const SocialLinks = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
`;

export const SocialLink = styled.a`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.85rem;
  background: rgba(11, 19, 32, 0.45);
  border: 1px solid ${borderSoft};
  border-radius: 4px;
  color: var(--text-secondary);
  text-decoration: none;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.35);
    background: rgba(0, 180, 216, 0.06);
    color: white;
    transform: translateX(2px);
  }

  @media (max-width: 360px) {
    gap: 0.55rem;
    padding: 0.6rem 0.7rem;
  }
`;

export const SocialIcon = styled.span`
  color: ${accent};
  font-size: 1.1rem;
  display: flex;
  align-items: center;
`;

/* ---------- electoral bar chart ---------- */

export const ElectionChart = styled.div`
  padding: 0.25rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

/* versión nueva: filas horizontales con barras */
export const BarRow = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(0, 1.4fr) 56px;
  grid-template-areas: 'label track value';
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
  min-width: 0;

  @media (max-width: 520px) {
    /* label + value en la fila superior; barra abajo a todo el ancho */
    grid-template-columns: 1fr 56px;
    grid-template-areas:
      'label value'
      'track track';
    gap: 0.35rem 0.6rem;
  }
`;


export const BarRowLabel = styled.div`
  grid-area: label;
  color: var(--text-secondary);
  font-size: 0.82rem;
  word-break: break-word;
`;

export const BarRowTrack = styled.div`
  grid-area: track;
  position: relative;
  height: 10px;
  background: rgba(11, 19, 32, 0.7);
  border: 1px solid ${borderSoft};
  border-radius: 2px;
  overflow: hidden;
`;

export const BarRowFill = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct }) => Math.max(1, Math.min(100, $pct))}%;
  background: ${({ color }) => color || accent};
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
`;

export const BarRowValue = styled.div`
  grid-area: value;
  text-align: right;
  font-family: ${mono};
  font-size: 0.82rem;
  color: white;
  letter-spacing: 0.02em;
`;

/* compat: viejas BarChart/BarGroup/Bar… se mantienen para ElectoralSection legacy */
export const BarChart = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  height: 200px;
  gap: 0.75rem;
`;

export const BarGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

export const BarLabel = styled.div`
  font-size: 0.72rem;
  text-align: center;
  margin-top: 0.5rem;
  color: var(--text-disabled);
  max-width: 110px;
  letter-spacing: 0.04em;
`;

export const Bar = styled.div`
  width: 34px;
  background: ${props => props.color || accent};
  position: relative;
  border-radius: 2px 2px 0 0;
  height: ${props => props.height || '10%'};
`;

export const BarValue = styled.div`
  position: absolute;
  top: -22px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${mono};
  font-size: 0.78rem;
  font-weight: 600;
  color: white;
`;

/* ---------- stats strip (métricas del header) ---------- */

export const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0;
  margin-top: 1.25rem;
  border-top: 1px solid ${borderSoft};
  border-bottom: 1px solid ${borderSoft};

  @media (max-width: 680px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

export const StatBlock = styled.div`
  padding: 0.8rem 1rem;
  border-right: 1px solid ${borderSoft};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 680px) {
    /* en 2 columnas, sólo la fila par de la derecha pierde el borde */
    &:nth-child(2n) { border-right: none; }
    border-bottom: 1px solid ${borderSoft};
    &:nth-last-child(-n+2) { border-bottom: none; }
  }
  @media (max-width: 380px) {
    border-right: none;
    border-bottom: 1px solid ${borderSoft};
    &:last-child { border-bottom: none; }
  }
`;

export const StatLabel = styled.div`
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-disabled);
  display: flex;
  align-items: center;
  gap: 0.35rem;

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    background: ${accent};
    border-radius: 50%;
  }
`;

export const StatValue = styled.div`
  font-family: ${mono};
  font-size: 1.35rem;
  color: white;
  font-weight: 600;
  line-height: 1.15;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem;
  word-break: break-word;

  /* Si el contenido es texto largo (no numérico), permitir que se reduzca. */
  @media (max-width: 1200px) {
    font-size: 1.2rem;
  }
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

export const StatDelta = styled.span`
  font-family: ${mono};
  font-size: 0.7rem;
  color: ${({ positive }) =>
    positive === true ? 'var(--alert-success)'
    : positive === false ? 'var(--alert-error)'
    : 'var(--text-disabled)'};
  margin: 0;
`;

export const ClassificationPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.25rem;
`;

export const ClassificationPill = styled.span`
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${accent};
  background: ${accentSoft};
  border: 1px solid rgba(0, 180, 216, 0.3);
  padding: 0.2rem 0.55rem;
  border-radius: 3px;
`;

export const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--alert-success);
  display: inline-block;
  animation: ${pulse} 2.2s ease-in-out infinite;
`;

/* ---------- social followers bar (opcional) ---------- */

export const FollowerCount = styled.span`
  font-family: ${mono};
  font-size: 0.78rem;
  color: ${accent};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.25rem;
  text-align: right;

  @media (max-width: 380px) {
    font-size: 0.72rem;
  }
`;

/* ---------- controversias (se mantiene, estilo técnico) ---------- */

export const ControversyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

export const ControversyItem = styled.div`
  border-left: 2px solid var(--alert-error);
  padding: 0.6rem 0.9rem;
  background: rgba(239, 71, 111, 0.05);
  border-radius: 0 4px 4px 0;
`;

export const ControversyTitle = styled.h4`
  margin: 0 0 0.3rem;
  color: white;
  font-size: 0.95rem;
`;

export const ControversyDescription = styled.p`
  color: var(--text-disabled);
  line-height: 1.5;
  font-size: 0.85rem;
  margin: 0;
`;
